import { useState } from "react";
import { orderRequests, OrderRequest } from "@/mocks/dashboardData";

const statusConfig: Record<OrderRequest["status"], { label: string; color: string; dot: string }> = {
  new: { label: "New", color: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  confirmed: { label: "Confirmed", color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  shipped: { label: "Shipped", color: "bg-sky-100 text-sky-700", dot: "bg-sky-500" },
  completed: { label: "Completed", color: "bg-gray-100 text-gray-600", dot: "bg-gray-400" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-600", dot: "bg-red-400" },
};

const OrdersTab = () => {
  const [orders, setOrders] = useState<OrderRequest[]>(orderRequests);
  const [filterStatus, setFilterStatus] = useState<"all" | OrderRequest["status"]>("all");
  const [selected, setSelected] = useState<OrderRequest | null>(null);

  const filtered = orders.filter((o) => filterStatus === "all" || o.status === filterStatus);

  const updateStatus = (id: string, status: OrderRequest["status"]) => {
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    if (selected?.id === id) setSelected((prev) => prev ? { ...prev, status } : null);
  };

  const statusTabs: Array<"all" | OrderRequest["status"]> = ["all", "new", "confirmed", "shipped", "completed"];

  return (
    <div className="flex gap-6">
      {/* List */}
      <div className="flex-1 min-w-0">
        {/* Filter tabs */}
        <div className="flex gap-1 bg-amber-50 border border-amber-100 rounded-xl p-1 mb-5 w-fit flex-wrap">
          {statusTabs.map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${
                filterStatus === s ? "bg-amber-500 text-white" : "text-gray-500 hover:text-amber-600"
              }`}
            >
              {s === "all" ? "All Orders" : s}
              {s !== "all" && (
                <span className="ml-1.5 bg-white/30 px-1 rounded-full">
                  {orders.filter((o) => o.status === s).length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {filtered.map((order) => {
            const sc = statusConfig[order.status];
            return (
              <button
                key={order.id}
                onClick={() => setSelected(order)}
                className={`w-full text-left bg-white border rounded-2xl p-4 transition-all cursor-pointer ${
                  selected?.id === order.id ? "border-amber-400" : "border-amber-100 hover:border-amber-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 rounded-full overflow-hidden border border-amber-100">
                    <img src={order.avatar} alt={order.buyerName} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="font-bold text-gray-900 text-sm">{order.buyerName}</p>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${sc.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                        {sc.label}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs mt-0.5 truncate">{order.product}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400 flex-wrap">
                      <span className={`font-semibold ${order.type === "sample" ? "text-amber-600" : "text-emerald-600"}`}>
                        {order.type === "sample" ? "Sample" : "Bulk"} · {order.qty} units
                      </span>
                      <span className="font-bold text-gray-800">${order.total.toLocaleString()}</span>
                      <span>{order.buyerCountry}</span>
                      <span>{order.date}</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Detail Panel */}
      {selected && (
        <div className="w-72 flex-shrink-0 bg-white border border-amber-100 rounded-2xl p-5 h-fit sticky top-24">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-black text-gray-900 text-sm">Order Detail</h4>
            <button onClick={() => setSelected(null)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
              <i className="ri-close-line"></i>
            </button>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full overflow-hidden border border-amber-100 flex-shrink-0">
              <img src={selected.avatar} alt={selected.buyerName} className="w-full h-full object-cover object-top" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">{selected.buyerName}</p>
              <p className="text-gray-400 text-xs">{selected.buyerCountry}</p>
            </div>
          </div>
          <div className="space-y-2 text-xs mb-4">
            {[
              { label: "Product", value: selected.product },
              { label: "Order Type", value: selected.type === "sample" ? "Sample Order" : "Bulk Order" },
              { label: "Quantity", value: `${selected.qty} units` },
              { label: "Unit Price", value: `$${selected.unitPrice}` },
              { label: "Total", value: `$${selected.total.toLocaleString()}` },
              { label: "Date", value: selected.date },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between py-1.5 border-b border-amber-50">
                <span className="text-gray-400">{label}</span>
                <span className="font-semibold text-gray-800 text-right max-w-[140px] truncate">{value}</span>
              </div>
            ))}
          </div>
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-600 mb-2">Update Status</p>
            <div className="grid grid-cols-2 gap-1.5">
              {(["confirmed", "shipped", "completed", "cancelled"] as OrderRequest["status"][]).map((s) => (
                <button
                  key={s}
                  onClick={() => updateStatus(selected.id, s)}
                  className={`py-1.5 rounded-lg text-xs font-semibold capitalize transition-all cursor-pointer whitespace-nowrap ${
                    selected.status === s
                      ? "bg-amber-500 text-white"
                      : "border border-amber-100 text-gray-600 hover:border-amber-300"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <button className="w-full bg-amber-50 border border-amber-200 text-amber-700 font-semibold text-xs py-2.5 rounded-xl hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5">
            <i className="ri-message-3-line"></i> Message Buyer
          </button>
        </div>
      )}
    </div>
  );
};

export default OrdersTab;
